import React from 'react';
import PropTypes from 'prop-types';

// Overrides
import {
  OnboardingPlannerViewSectionProvider,
} from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Onboarding/Sections/ViewSectionContext';
import { ModalViewProvider } from 'Views/Onboarding/Contexts/ModalViewContext';
import AumInformationLayoutOverride
  from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Onboarding/Sections/AumInformation/MetaOverride';
import InvestorProfileViewLayout
  from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Onboarding/Sections/InvestorProfile/Layout';
import { GoalSummaryOverride } from 'Views/Onboarding/Pages/Survey/SurveyStep/StepLibrary/GoalSummary/MetaOverride';
import {
  GoalsSectionLayout,
} from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Onboarding/Sections/Goals/GoalsSectionLayout';
import {
  MissingInformationOverride,
} from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Onboarding/Sections/missingInformation/metaOverrideSet';
import {
  AUMResponsesLayout,
} from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Onboarding/Sections/aumResponses/layout';
import {
  AUMResponsesOverride,
} from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Onboarding/Sections/aumResponses/metaOverrideSet';
import DefaultLayoutOverride from './Sections/Default/MetaOverride';

// Layouts
import FamilyInformationLayout from './Sections/FamilyInformation/Layout';
import AumInformationLayout from './Sections/AumInformation/Layout';
import MoneyMindsetLayout from './Sections/MoneyMindset/Layout';
import BalanceSheetLayout from './Sections/BalanceSheet/Layout';
import AssetsAndLiabilitiesLayout from './Sections/AssetsAndLiabilities/Layout';
import { OnboardingViewSectionIncomeLayout } from './Sections/Income/OnboardingViewSectionIncomeLayout';
import ExpensesLayout from './Sections/Expenses/Layout';
import EstatePlanningLayout from './Sections/EstatePlanning/Layout';
import DefaultLayout from './Sections/Default/Layout';
import IncompleteLayout from './Sections/Incomplete/Layout';
import RiskToleranceManualLayout from './Sections/RiskToleranceManualLayout';
import NoteLayout from './Sections/Note/Layout';
import EQFResponseLayout from './Sections/EQFResponses/Layout';
import { MissingInformationLayout } from './Sections/missingInformation/layout';

import ViewForm from './ViewForm';

const LAYOUTS = {
  familyInformation: 'familyInformation',
  welcomeIntro: 'welcomeIntro',
  goalSummary: 'goalSummary',
  balanceSheet: 'balanceSheet',
  onboardingAssetsAndLiabilitiesWelcome: 'onboardingAssetsAndLiabilitiesWelcome',
  onboardingAssetsAndLiabilitiesWelcomeNew: 'onboardingAssetsAndLiabilitiesWelcomeNew',
  incomeInformation: 'incomeInformation',
  expenseWelcome: 'expenseWelcome',
  onboardingPlannerViewEstatePlanning: 'onboardingPlannerViewEstatePlanning',
  InvestorProfileInvestmentSection: 'InvestorProfileInvestmentSection',
  onboardingPlannerViewAUMInformation: 'onboardingPlannerViewAUMInformation',
  onboardingPlannerViewAUMResponses: 'onboardingPlannerViewAUMResponses',
  riskToleranceManual: 'riskToleranceManual',
  note: 'note',
  eqfMissionControlQuizResponseCollection: 'eqfResponses',
  missingInformation: 'missingInformation',
};

const getLayout = (headResourceType) => {
  switch (headResourceType) {
    case LAYOUTS.familyInformation:
      return {
        layout: FamilyInformationLayout,
      };
    case LAYOUTS.onboardingPlannerViewAUMInformation:
      return {
        layout: AumInformationLayout,
        metaOverrideSet: AumInformationLayoutOverride,
      };
    case LAYOUTS.onboardingPlannerViewAUMResponses:
      return {
        layout: AUMResponsesLayout,
        metaOverrideSet: AUMResponsesOverride,
      };
    case LAYOUTS.welcomeIntro:
      return {
        layout: MoneyMindsetLayout,
      };
    case LAYOUTS.goalSummary:
      return {
        layout: GoalsSectionLayout,
        metaOverrideSet: GoalSummaryOverride,
      };
    case LAYOUTS.onboardingAssetsAndLiabilitiesWelcome:
      return {
        layout: BalanceSheetLayout,
      };
    case LAYOUTS.onboardingAssetsAndLiabilitiesWelcomeNew:
      return {
        layout: AssetsAndLiabilitiesLayout,
      };
    case LAYOUTS.incomeInformation:
      return {
        layout: OnboardingViewSectionIncomeLayout,
      };
    case LAYOUTS.expenseWelcome:
      return {
        layout: ExpensesLayout,
      };
    case LAYOUTS.onboardingPlannerViewEstatePlanning:
      return {
        layout: EstatePlanningLayout,
        metaOverrideSet: DefaultLayoutOverride,
      };
    case LAYOUTS.InvestorProfileInvestmentSection:
      return {
        layout: InvestorProfileViewLayout,
        metaOverrideSet: DefaultLayoutOverride,
      };
    case LAYOUTS.riskToleranceManual:
      return {
        layout: RiskToleranceManualLayout,
      };
    case LAYOUTS.note:
      return {
        layout: NoteLayout,
      };
    case LAYOUTS.eqfMissionControlQuizResponseCollection:
      return {
        layout: EQFResponseLayout,
      };
    case LAYOUTS.missingInformation:
      return {
        layout: MissingInformationLayout,
        metaOverrideSet: MissingInformationOverride,
      };
    default:
      return {
        metaOverrideSet: DefaultLayoutOverride,
        layout: DefaultLayout,
      };
  }
};

const ViewLayoutPicker = ({ sectionResource }) => {
  const headResourceType = sectionResource?.type();
  const {
    layout,
    metaOverrideSet,
  } = getLayout(headResourceType);

  return (
    <OnboardingPlannerViewSectionProvider viewSectionResource={sectionResource}>
      <ModalViewProvider>
        <ViewForm
          key={headResourceType}
          layout={sectionResource.attr('isComplete') === 'true' ? layout : IncompleteLayout}
          metaOverrideSet={metaOverrideSet}
          sectionResource={sectionResource}
        />
      </ModalViewProvider>
    </OnboardingPlannerViewSectionProvider>
  );
};

ViewLayoutPicker.propTypes = {
  sectionResource: PropTypes.object.isRequired,
};

export default ViewLayoutPicker;
